import { motion } from 'framer-motion';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getItems, getFeaturedItems } from '../api/api';
import HeroSection from '../components/home/HeroSection';
import MutualMatches from '../components/home/MutualMatches';
import PopularItems from '../components/home/PopularItems';
import { useLanguage } from '../contexts/LanguageContext';
import FloatingBackground from '../components/home/FloatingBackground';
import SEO from '../components/common/SEO';
import { useNavigate } from 'react-router-dom';
import { useEffect } from 'react';

import HomeSidebar from '../components/home/HomeSidebar';

export default function Home() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const getCookie = (name) => {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop().split(';').shift();
    return null;
  };

  const lastCategory = getCookie('last_category_search');

  useEffect(() => {
    // First time visitor redirect to /welcome terms agreement page
    if (!localStorage.getItem('hasAcceptedTerms')) {
      navigate('/welcome');
    }
  }, [navigate]);

  const { 
    data: featuredItems, 
    isLoading: isLoadingFeatured, 
    refetch: refreshFeatured 
  } = useQuery({
    queryKey: ['items', 'featured', lastCategory],
    queryFn: () => getFeaturedItems(8, lastCategory),
    staleTime: 0,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
  });

  // Fetch Newest / Lately added items (9 items)
  const { 
    data: newestItemsResult, 
    isLoading: isLoadingNewest, 
    refetch: refreshNewest 
  } = useQuery({
    queryKey: ['items', 'newest'],
    queryFn: () => getItems({ limit: 9 }),
    staleTime: 0,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
  });

  const handleRefreshNewest = async () => {
    await queryClient.invalidateQueries({ queryKey: ['items', 'newest'] });
    refreshNewest();
  };

  const handleRefreshFeatured = async () => {
    await queryClient.invalidateQueries({ queryKey: ['items', 'featured'] });
    refreshFeatured();
  };

  const newestItems = newestItemsResult?.items || [];

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      },
    },
  };

  const organizationJsonLd = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "Ahlafot",
    "url": "https://ahlafot.co.il",
    "logo": "https://ahlafot.co.il/favicon.jpg",
    "description": t('homeTitleDesc', 'Ahlafot - Trade Anything for Anything. The ultimate marketplace for items exchange.'),
    "sameAs": [
      "https://www.facebook.com/ahlafot",
      "https://www.instagram.com/ahlafot"
    ]
  };

  return (
    <>
      <SEO 
        title={t('home', 'Home')}
        description={t('homeTitleDesc', 'Ahlafot - Trade Anything for Anything. The ultimate marketplace for items exchange.')}
        jsonLd={organizationJsonLd}
        canonical="https://ahlafot.co.il"
      />
      <FloatingBackground />
      <div className="space-y-16 pb-20 relative z-10">
        <div>
          <HeroSection />
        </div>
        
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8 items-start relative">
            
            {/* MAIN CONTENT (3 Columns on Desktop) */}
            <div className="lg:col-span-3 space-y-16">
              {/* Mutual Matches Section */}
              <motion.div variants={itemVariants}>
                <MutualMatches />
              </motion.div>

              {/* Promoted Items Section */}
              {featuredItems?.items && featuredItems.items.length > 0 && (
                <motion.div variants={itemVariants}>
                  <PopularItems 
                      items={featuredItems.items} 
                      title={t('promotedItems', 'Promoted Items 🌟')}
                      onRefresh={handleRefreshFeatured}
                      isLoading={isLoadingFeatured}
                      viewMode="list"
                  />
                </motion.div>
              )}

              {/* Newest Items Section */}
              <motion.div variants={itemVariants}>
                <PopularItems 
                    items={newestItems} 
                    title={t('newestItems', 'Newest Items')}
                    onRefresh={handleRefreshNewest}
                    isLoading={isLoadingNewest}
                    viewMode="list"
                />
              </motion.div>
            </div>

            {/* SIDEBAR (Sticky Column on Desktop starting at Listing Level) */}
            <div className="lg:col-span-1 sticky top-24 self-start">
              <HomeSidebar />
            </div>

          </div>
        </div>
      </div>
    </>
  );
}
